import { useEffect, useMemo, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { UseMutateAsyncFunction } from "react-query";
import { toast } from "react-toastify";

import { faX } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { FieldType } from "../../constants";
import { DataFormFieldProvider } from "../../contexts/dataFormFieldContext";
import { CustomDataDto } from "../../types/dto";
import { ModalSetting } from "../../types/setting";
import { DataForm, setDataFormField } from "../dataForm/dataForm";
import { FieldDropdown } from "../dataForm/fieldDropdown";
import { FieldTextArea } from "../dataForm/fieldTextArea";
import { FormControlWrapper } from "../dataForm/formControlWrapper";
import { RibbonButtons } from "../dataTable/dataTableRibbonButtons";
import {
	datedButtonSetting,
	datedIconSetting,
	datedMessageSetting,
	enforceMinVerSetting,
} from "./customDataModalSettings";

enum CustomDataType {
	Arbitrary = "Arbitrary",
	DatedMessage = "DatedMessage",
	EnforceMinimumVersion = "EnforceMinimumVersion",
	DatedIcon = "DatedIcon",
	DatedButton = "DatedButton",
}

const customDataTypeDropdownValues: { id: string; label: string }[] = [];
for (const t in CustomDataType) {
	customDataTypeDropdownValues.push({ id: t, label: t });
}

export const CustomDataModal = ({
	initialCustomData,
	isModalOpen,
	setIsModalOpen,
	submitEditCustomData,
	submitDeleteCustomData,
}: {
	initialCustomData: any;
	isModalOpen: boolean;
	setIsModalOpen: (isOpen: boolean) => void;
	submitEditCustomData: UseMutateAsyncFunction<
		any,
		unknown,
		CustomDataDto,
		unknown
	>;
	submitDeleteCustomData: UseMutateAsyncFunction<
		any,
		unknown,
		CustomDataDto,
		unknown
	>;
}) => {
	const [metaData, setMetadata] = useState<any>(initialCustomData);
	const [dataObject, setDataObject] = useState<any>({});
	const [dataObjectString, setDataObjectString] = useState<any>("{}");

	const setNiceDataObjStr = (jsonStr: string) => {
		try {
			const obj = JSON.parse(jsonStr);
			setDataObjectString(JSON.stringify(obj, null, 2));
		} catch {
			setDataObjectString(jsonStr);
		}
	};

	const [customDataType, setCustomDataType] = useState<any>(
		CustomDataType.Arbitrary,
	);
	const [arbitraryJsonError, setArbitraryJsonError] = useState<string | null>(
		null,
	);

	useEffect(() => {
		const newDataStr = initialCustomData?.data;
		setMetadata(initialCustomData);
		setNiceDataObjStr(newDataStr || "{}");

		if (!newDataStr) return;
		try {
			const newDataObj = JSON.parse(newDataStr);
			setDataObject(newDataObj);
			const isKnownType = Object.values(CustomDataType).includes(
				newDataObj.type,
			);
			setCustomDataType(
				isKnownType ? newDataObj.type : CustomDataType.Arbitrary,
			);
		} catch (e: any) {
			setDataObjectString("{}");
			setDataObject({});
			setCustomDataType(CustomDataType.Arbitrary);

			console.error(`Json: ${newDataStr}\n`, e);
			toast.error("Failed to parse custom data JSON");
		}
	}, [initialCustomData]);

	const setDataObjectFromTextArea = (value: string) => {
		setNiceDataObjStr(value);

		try {
			const obj = JSON.parse(value);
			setDataObject(obj);
			setArbitraryJsonError(null);
		} catch (e: any) {
			console.log(e);
			setArbitraryJsonError(e?.message);
		}
	};

	const closeModal = () => {
		setArbitraryJsonError("");
		setIsModalOpen(false);
		setMetadata({});
		setDataObjectString("{}");
		setDataObject({});
	};

	const metadataSetting: ModalSetting = {
		fields: [
			[
				{
					field: "id",
					isMandatory: true,
					isDisabled: true,
					title: "Id",
				},
			],
			[
				{
					field: "name",
					isMandatory: true,
					title: "Name",
					placeholder: "Enter Name",
				},
			],
			[
				{
					field: "description",
					isMandatory: false,
					title: "Description",
					placeholder: "Enter Description",
				},
			],
			[
				{
					field: "executionPriority",
					type: FieldType.NUMBER,
					isMandatory: true,
					title: "Execution Priority",
					placeholder: "Enter Execution Priority",
				},
			],
		],
	};

	const setDataObjectField = (field: any) => (value: any) => {
		let newDataObj: any;

		setDataObject((oldObj: any) => {
			newDataObj = { ...oldObj };
			newDataObj[field] = value;
			return newDataObj;
		});

		setDataObjectString(() => JSON.stringify(newDataObj, null, 2));
	};

	const changeableDataFields = useMemo(() => {
		switch (customDataType) {
			case CustomDataType.DatedMessage:
				return (
					<DataForm
						currentFormData={dataObject}
						setFormData={setDataObjectField}
						editorSetting={datedMessageSetting}
					/>
				);
			case CustomDataType.EnforceMinimumVersion:
				return (
					<DataForm
						currentFormData={dataObject}
						setFormData={setDataObjectField}
						editorSetting={enforceMinVerSetting}
					/>
				);
			case CustomDataType.DatedIcon:
				return (
					<DataForm
						currentFormData={dataObject}
						setFormData={setDataObjectField}
						editorSetting={datedIconSetting}
					/>
				);
			case CustomDataType.DatedButton:
				return (
					<DataForm
						currentFormData={dataObject}
						setFormData={setDataObjectField}
						editorSetting={datedButtonSetting}
					/>
				);
			case CustomDataType.Arbitrary:
			default:
				return (
					<DataFormFieldProvider
						fieldSetting={{ field: "", title: "Arbitrary JSON" }}
					>
						<FieldTextArea
							isForced={false}
							error={arbitraryJsonError}
							setValue={setDataObjectFromTextArea}
							fieldData={dataObjectString}
							autoFocus={false}
							isDisabled={false}
							maxLength={undefined}
							tooltip={undefined}
						/>
					</DataFormFieldProvider>
				);
		}
	}, [customDataType, arbitraryJsonError, dataObject, dataObjectString]);

	const getCompleteCustomData = () => {
		const completeCustomData =
			customDataType === CustomDataType.Arbitrary
				? {
						...dataObject,
					}
				: {
						type: customDataType,
						...dataObject,
					};
		return completeCustomData;
	};

	return (
		<Modal
			as={Modal.Dialog}
			centered
			show={isModalOpen}
			onHide={closeModal}
			className="modal-lg"
		>
			<Modal.Header>
				<Modal.Title>
					Edit Custom Data: {metaData?.id}
					{metaData?.name ? ` - ${metaData?.name}` : ""}
				</Modal.Title>
				<RibbonButtons>
					<Button
						onClick={closeModal}
						variant="outline-primary"
						size="sm"
						aria-label="Close"
					>
						<FontAwesomeIcon icon={faX} />
					</Button>
				</RibbonButtons>
			</Modal.Header>
			<Modal.Body>
				<DataForm
					currentFormData={metaData}
					setFormData={setDataFormField(setMetadata)}
					editorSetting={metadataSetting}
				/>
				<DataFormFieldProvider
					fieldSetting={{ field: "", title: "Type" }}
				>
					<FieldDropdown
						isForced={true}
						dropdownData={customDataTypeDropdownValues}
						isMandatory={true}
						fieldData={customDataType}
						setValue={setCustomDataType}
						// mandatory to avoid ts errors
						autoFocus={false}
						error={undefined}
						isDisabled={false}
						tooltip={undefined}
					/>
				</DataFormFieldProvider>
				{changeableDataFields}
				<DataFormFieldProvider
					fieldSetting={{ field: "", title: "JSON" }}
				>
					<FormControlWrapper data-testid="test-fieldTextArea">
						<pre data-testid="json-preview">
							{JSON.stringify(
								getCompleteCustomData(),
								undefined,
								2,
							)}
						</pre>
					</FormControlWrapper>
				</DataFormFieldProvider>
			</Modal.Body>
			<Modal.Footer>
				<Button
					className="ms-auto"
					variant="secondary"
					onClick={() => {
						if (metaData) submitDeleteCustomData(metaData);
						closeModal();
					}}
				>
					Delete
				</Button>
				<Button
					disabled={
						customDataType === CustomDataType.Arbitrary &&
						arbitraryJsonError !== null
					}
					variant="primary"
					onClick={() => {
						const completeCustomData = {
							...metaData,
							data: JSON.stringify(getCompleteCustomData()),
						};
						submitEditCustomData(completeCustomData);
						closeModal();
					}}
				>
					Save
				</Button>
			</Modal.Footer>
		</Modal>
	);
};
